/*
 * GA4 dataLayer — begin_checkout + view_cart
 * ------------------------------------------------------------------
 * Pushes GA4 ecommerce events to window.dataLayer for GTM (GTM-5XTWSWR).
 *
 * Events: begin_checkout, view_cart, view_item, add_to_cart.
 *
 *   view_item + add_to_cart follow the client's DataLayer sheet: the Shopify
 *   Google & YouTube app event shape plus item_list_id / item_list_name
 *   (see "List attribution" below). Each is switched by a flag in
 *   window.ga4Config (emitViewItem / emitAddToCart, layout/theme.liquid).
 *   The Stape Conversion Tracking app embed is enabled on this store; confirm
 *   in GTM Preview that it does not push the same events to the page
 *   dataLayer, or every view/add would count twice in GA4 and Google Ads.
 *
 * Design constraints this file works around:
 *   - Checkout is Gokwik (an overlay), not native Shopify. snippets/gokwik.liquid
 *     is vendor-managed and carries "Do Not Touch This Line" markers, so we
 *     attach our own passive listeners instead of editing it.
 *   - Gokwik's MutationObserver re-binds checkout buttons roughly every 700ms,
 *     so per-element listeners would be re-added repeatedly. We use a single
 *     delegated capture-phase document listener instead.
 *   - The cart drawer is a custom element in custom-cart-drawer.js whose
 *     open() has several early-return recovery branches. Rather than patch it,
 *     we observe the resulting class change ("active") on the element.
 */
(function () {
  "use strict";

  window.dataLayer = window.dataLayer || [];

  var CURRENCY = (window.ga4Config && window.ga4Config.currency) || "INR";

  // Gokwik's own click handler guards with `clicked` and resets it on
  // modal_closed. We mirror that: one begin_checkout per opened modal.
  var checkoutInFlight = false;

  function toMajorUnits(cents) {
    var n = typeof cents === "number" ? cents : parseInt(cents, 10);
    if (isNaN(n)) return 0;
    return Math.round(n) / 100;
  }

  /* Shopify cart.js line -> GA4 item. Uses the modern GA4 field names
   * (item_id / item_name). The client workbook mixes legacy names (id/name)
   * for some events; if GTM variable mapping expects legacy, remap in GTM
   * rather than here, so this file stays spec-correct. */
  function lineToItem(line, index) {
    var item = {
      // Shape differs by source: /cart.js lines carry variant_id, whereas a
      // /cart/add.js response line carries the variant in `id`. Handle both.
      item_id: String(line.product_id || ""),
      item_name: line.product_title || line.title || "",
      item_brand: line.vendor || "",
      item_variant: line.variant_title || "",
      variant_id: String(line.variant_id || line.id || ""),
      sku: line.sku || "",
      price: toMajorUnits(line.final_price != null ? line.final_price : line.price),
      quantity: line.quantity || 1,
      index: index
    };

    // List attribution. NOTE: nothing in the theme sets these line properties
    // yet, so these fields are currently never populated — this is the
    // forward-compatible half of the work. To make item_list_name/id flow
    // through to checkout, the add-to-cart paths must attach
    // properties[_item_list_name] / [_item_list_id] at the point of add.
    // That is the separate "list attribution" task.
    var props = line.properties || {};
    if (props._item_list_name) item.item_list_name = props._item_list_name;
    if (props._item_list_id) item.item_list_id = props._item_list_id;

    return item;
  }

  function buildItems(cart) {
    var lines = (cart && cart.items) || [];
    return lines.map(lineToItem);
  }

  function fetchCart() {
    return fetch("/cart.js", {
      credentials: "same-origin",
      headers: { Accept: "application/json" }
    }).then(function (r) {
      if (!r || !r.ok) throw new Error("cart fetch failed");
      return r.json();
    });
  }

  function push(eventName, cart) {
    var items = buildItems(cart);
    if (!items.length) return; // never push an empty ecommerce event

    window.dataLayer.push({
      event: eventName,
      eventModel: {
        send_to: (window.ga4Config && window.ga4Config.sendTo) || undefined,
        value: toMajorUnits(cart.total_price),
        currency: cart.currency || CURRENCY,
        items: items
      }
    });
  }

  function pushFromCart(eventName) {
    return fetchCart()
      .then(function (cart) {
        push(eventName, cart);
        return cart;
      })
      .catch(function () {
        /* Analytics must never break the funnel. Swallow. */
      });
  }

  /* ---------------------------------------------------------------
   * begin_checkout
   * ---------------------------------------------------------------
   * Fires on the real Gokwik checkout triggers. The legacy Google Ads
   * handler in theme.liquid binds `.cart--button`, which no longer exists
   * in live markup (all occurrences are commented out) — that tracking has
   * been firing zero times. The live triggers are these two.
   *
   * NOT included: payment_type and coupon. Those are only known inside the
   * Gokwik overlay and cannot be read from theme code. They need Gokwik SDK
   * callbacks to populate.
   */
  var CHECKOUT_SELECTOR = "#gokwik-buy-now, .gokwik-checkout button";

  document.addEventListener(
    "click",
    function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== "function") return;

      var trigger = target.closest(CHECKOUT_SELECTOR);
      if (!trigger) return;
      if (trigger.disabled || trigger.classList.contains("disabled")) return;
      if (checkoutInFlight) return;

      checkoutInFlight = true;
      pushFromCart("begin_checkout");
    },
    true // capture: run before Gokwik's own handler / any stopPropagation
  );

  // Re-arm when the Gokwik modal closes, matching gokwik.liquid's reset.
  window.addEventListener("gokwikLoaded", function () {
    if (window.gokwikSdk && typeof window.gokwikSdk.on === "function") {
      window.gokwikSdk.on("modal_closed", function () {
        checkoutInFlight = false;
      });
    }
  });

  // Safety net: if the overlay never opened (SDK not ready, user bounced),
  // don't permanently suppress future begin_checkout events.
  window.addEventListener("pageshow", function () {
    checkoutInFlight = false;
  });

  /* ---------------------------------------------------------------
   * add_to_cart
   * ---------------------------------------------------------------
   * Fires ONLY on a server-confirmed /cart/add success — never on click.
   * The legacy Google Ads handlers in theme.liquid bind `.add-cart-btn`
   * clicks and fire even when the add fails, and are bound twice on the
   * same selector. This does not repeat that: no click binding at all.
   *
   * Why intercept the network instead of binding buttons: there are 9
   * distinct add-to-cart code paths in this theme (PDP, sticky bar, quick
   * add, bundles, BYOB, cart-drawer cross-sell, sibling PDP, grid stepper,
   * stock Dawn product-form) and `.add-cart-btn` appears in 20 files.
   * Wrapping fetch/XHR catches all of them, including third-party widgets.
   *
   * Deliberate exclusion: the free-gift engine (assets/cart-free-gift.js)
   * auto-adds promo gifts the user never chose. Those carry the
   * X-Cart-Free-Gift header and must NOT be reported as add_to_cart —
   * counting them would invent user intent that did not happen.
   *
   * NOTE ON fetch WRAPPING ORDER: theme.liquid also monkey-patches
   * window.fetch ~3s after load (Judge.me). Both wrappers delegate to the
   * previous window.fetch, so chaining is safe in either order. We tag our
   * wrapper so we never double-wrap on re-execution.
   */
  function addToCartEnabled() {
    return Boolean(window.ga4Config && window.ga4Config.emitAddToCart);
  }

  function isCartAddUrl(url) {
    if (!url) return false;
    try {
      return /\/cart\/add(\.js)?$/.test(new URL(url, window.location.origin).pathname);
    } catch (e) {
      return /\/cart\/add(\.js)?(\?|$)/.test(String(url));
    }
  }

  function headerValue(input, init, name) {
    try {
      var lower = name.toLowerCase();
      var h = (init && init.headers) || (input && input.headers);
      if (!h) return "";
      if (typeof h.get === "function") return h.get(name) || "";
      if (Array.isArray(h)) {
        for (var i = 0; i < h.length; i++) {
          if (h[i] && String(h[i][0]).toLowerCase() === lower) return String(h[i][1]);
        }
        return "";
      }
      for (var k in h) {
        if (String(k).toLowerCase() === lower) return String(h[k]);
      }
      return "";
    } catch (e) {
      return "";
    }
  }

  // Free-gift auto-adds are not user intent — never report them.
  function isFreeGiftRequest(input, init) {
    return headerValue(input, init, "X-Cart-Free-Gift") === "1";
  }

  /* add_to_cart in the client's DataLayer-sheet format (Shopify Google &
   * YouTube app shape + item_list_id/_name). value / ecomm_totalvalue are
   * the TOTAL CART value after the add, per the sheet, so /cart.js is read
   * once the add has succeeded. The list is the one captured at click time. */
  function pushAddToCart(payload) {
    if (!addToCartEnabled()) return;

    var lines = payload
      ? Array.isArray(payload.items) ? payload.items : [payload]
      : [];
    lines = lines.filter(function (l) {
      return l && (l.product_id || l.variant_id || l.id);
    });
    if (!lines.length) return;

    var list = takeAddList();
    var items = lines.map(function (l) {
      return specItem(
        {
          productId: l.product_id,
          variantId: l.variant_id || l.id,
          name: l.product_title || l.title,
          brand: l.vendor,
          category: l.product_type,
          price: l.final_price != null ? l.final_price : l.price,
          variant: l.variant_title,
          quantity: l.quantity,
          sku: l.sku
        },
        list
      );
    });

    fetchCart()
      .catch(function () {
        return null;
      })
      .then(function (cart) {
        // Prices come from /cart.js, not the add response: some add paths
        // answer with rupees ("750.00") instead of paise (75000), which read
        // as ₹7.5. /cart.js is always paise. Matched by variant id.
        if (cart && Array.isArray(cart.items)) {
          items.forEach(function (it) {
            for (var i = 0; i < cart.items.length; i++) {
              var cl = cart.items[i];
              if (String(cl.variant_id) === it.variant_id && !(cl.properties && cl.properties._free_gift)) {
                it.price = toMajorUnits(cl.final_price != null ? cl.final_price : cl.price);
                break;
              }
            }
          });
        }
        var total = cart
          ? toMajorUnits(cart.total_price)
          : items.reduce(function (s, it) {
              return s + it.price * (it.quantity || 1);
            }, 0);
        pushSpecEvent("add_to_cart", "product_added_to_cart", "cart", total, items);
      })
      .catch(function () {
        /* Analytics must never break the funnel. */
      });
  }

  /* ---------------------------------------------------------------
   * Client DataLayer-sheet helpers (view_item / add_to_cart)
   * --------------------------------------------------------------- */
  function sendToFor(eventName) {
    var s = window.ga4Config && window.ga4Config.sendTo;
    if (!s) return undefined;
    if (typeof s === "string") return s || undefined;
    return s[eventName] || undefined;
  }

  function uuid() {
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
      return window.crypto.randomUUID();
    }
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  function readCookie(name) {
    var m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
    return m ? decodeURIComponent(m[1]) : "";
  }

  // Shopify's client id (same source the Google app's "shp.<uuid>" uses).
  function extClientId() {
    var y = readCookie("_shopify_y");
    return y ? "shp." + y : undefined;
  }

  // Item in the sheet's shape. id = shopify_<country>_<product>_<variant>,
  // matching the Google Merchant feed id the sheet's example uses.
  function specItem(src, list) {
    var country = (window.ga4Config && window.ga4Config.itemIdCountry) || "ZZ";
    var variantTitle = src.variant && src.variant !== "Default Title" ? src.variant : null;
    return {
      id: "shopify_" + country + "_" + (src.productId || "") + "_" + (src.variantId || ""),
      name: src.name || "",
      brand: src.brand || "",
      category: src.category || "",
      price: toMajorUnits(src.price),
      variant: variantTitle,
      quantity: src.quantity || 1,
      sku: src.sku || "",
      variant_id: String(src.variantId || ""),
      item_list_id: (list && list.id) || "",
      item_list_name: (list && list.name) || ""
    };
  }

  function pushSpecEvent(eventName, shopifyEventName, pageType, value, items) {
    if (!items.length) return;
    window.dataLayer.push({
      event: eventName,
      eventModel: {
        send_to: sendToFor(eventName),
        shopify_event_name: shopifyEventName,
        developer_id: { dNzYwYj: true },
        event_id: "sh-" + uuid(),
        ext_client_id: extClientId(),
        ecomm_prodid: items.map(function (it) {
          return it.id;
        }),
        ecomm_totalvalue: Math.round(value * 100) / 100,
        ecomm_pagetype: pageType,
        value: Math.round(value * 100) / 100,
        currency: CURRENCY,
        items: items
      }
    });
  }

  /* ---------------------------------------------------------------
   * List attribution (item_list_id / item_list_name)
   * ---------------------------------------------------------------
   * "Where was the product when the shopper acted on it". Resolved from the
   * DOM around the click, in this order:
   *   1. an explicit data-item-list-name (+ data-item-list-id) on any
   *      ancestor — set this on a section to force its name;
   *   2. the cart drawer                         -> "cart drawer";
   *   3. a lightning-sale section                -> "lightning sale";
   *   4. the main product section on a PDP       -> "pdp page";
   *   5. the section's first heading text, e.g. "face wash";
   *   6. the section's key from its id, e.g. "feature_collection_face_wash";
   *   7. "<template> page".
   * The id is the name slugged ("face wash" -> "face_wash").
   *
   * add_to_cart uses the list captured on the add button's click (the add
   * request follows within moments). view_item uses the list of the product
   * link that led to the PDP, kept in sessionStorage per product handle. */
  var ADD_LIST_TTL = 10000;
  var VIEW_LIST_TTL = 30 * 60 * 1000;
  var VIEW_LIST_KEY = "ga4:viewList";
  var pendingAddList = null;

  function slug(text) {
    return String(text || "")
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
  }

  function templateName() {
    return (window.ga4Config && window.ga4Config.template) || "";
  }

  // The section's own title. Custom sections often render it in a div
  // (.home-cat-name on Bestsellers), so those classes are checked too.
  // Anything inside a product card / link / form is a PRODUCT name, not the
  // section's, and is skipped.
  var HEADING_SELECTOR =
    "h1, h2, h3, .home-cat-name, .fc-heading, .section-heading, .section-title, .title";

  function sectionHeading(section) {
    var nodes = section.querySelectorAll(HEADING_SELECTOR);
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      if (n.closest("a[href*='/products/'], form, [class*='card'], .home-cat-iteam, .prduct-filter")) continue;
      var text = n.textContent.replace(/\s+/g, " ").trim();
      if (text && text.length <= 60) return text;
    }
    return "";
  }

  function resolveList(el) {
    if (!el || typeof el.closest !== "function") return null;

    var tagged = el.closest("[data-item-list-name]");
    if (tagged) {
      var tName = tagged.getAttribute("data-item-list-name");
      return { name: tName, id: tagged.getAttribute("data-item-list-id") || slug(tName) };
    }

    if (el.closest("cart-drawer, #CartDrawer")) {
      return { name: "cart drawer", id: "cart_drawer" };
    }

    var section = el.closest(".shopify-section, [id^='shopify-section-']");
    var sectionId = section ? section.id || "" : "";
    var key = sectionId.indexOf("__") > -1 ? sectionId.split("__").pop() : sectionId.replace(/^shopify-section-/, "");
    // Strip Shopify's random 6-char block suffix ("face_wash_TkafNn" ->
    // "face_wash"). It always carries an uppercase letter or digit, so real
    // words like "_banner" are left alone.
    key = key.replace(/_(?=[A-Za-z0-9]*[A-Z0-9])[A-Za-z0-9]{6}$/, "");

    // Section id only: product CARDS carry lightning classes in every grid,
    // which would mislabel ordinary sections.
    if (/lightning/i.test(sectionId)) {
      return { name: "lightning sale", id: "lightning_sale" };
    }

    if (templateName() === "product" && /main/i.test(key)) {
      return { name: "pdp page", id: "pdp_page" };
    }

    if (section) {
      var hText = sectionHeading(section);
      if (hText) {
        return { name: hText.toLowerCase(), id: slug(hText) };
      }
      // Older sections have machine ids ("165814162682140f29") — never a name.
      if (key && !/\d{6,}/.test(key)) return { name: key.replace(/_/g, " "), id: slug(key) };
    }

    var tpl = templateName();
    return tpl ? { name: tpl + " page", id: slug(tpl + "_page") } : null;
  }

  function handleFromHref(href) {
    try {
      var m = new URL(href, window.location.origin).pathname.match(/\/products\/([^/?#]+)/);
      return m ? decodeURIComponent(m[1]) : "";
    } catch (e) {
      return "";
    }
  }

  function readViewLists() {
    try {
      return JSON.parse(window.sessionStorage.getItem(VIEW_LIST_KEY) || "{}") || {};
    } catch (e) {
      return {};
    }
  }

  function rememberViewList(handle, list) {
    if (!handle || !list) return;
    try {
      var all = readViewLists();
      all[handle] = { name: list.name, id: list.id, t: Date.now() };
      window.sessionStorage.setItem(VIEW_LIST_KEY, JSON.stringify(all));
    } catch (e) {
      /* storage blocked — view_item just goes without a list */
    }
  }

  function takeAddList() {
    var list = pendingAddList;
    pendingAddList = null;
    if (list && Date.now() - list.t <= ADD_LIST_TTL) return list;
    // No click captured (e.g. a scripted add): on a PDP it was the PDP.
    if (templateName() === "product") return { name: "pdp page", id: "pdp_page" };
    return null;
  }

  // One capture-phase listener records the list for both events: product
  // links (for the next PDP's view_item) and add-to-cart controls.
  document.addEventListener(
    "click",
    function (event) {
      try {
        var target = event.target;
        if (!target || typeof target.closest !== "function") return;

        var link = target.closest("a[href*='/products/']");
        if (link) {
          rememberViewList(handleFromHref(link.getAttribute("href")), resolveList(link));
        }

        var adder = target.closest(
          "button, [type='submit'], [role='button'], .add-cart-btn, [name='add']"
        );
        if (adder) {
          var list = resolveList(adder);
          if (list) {
            pendingAddList = { name: list.name, id: list.id, t: Date.now() };
          }
        }
      } catch (e) {
        /* Analytics must never break a click. */
      }
    },
    true
  );

  /* ---------------------------------------------------------------
   * view_item — fires once when a product page loads.
   * --------------------------------------------------------------- */
  function initViewItem() {
    try {
      pushViewItem();
    } catch (e) {
      /* Analytics must never break the page. */
    }
  }

  function pushViewItem() {
    var cfg = window.ga4Config || {};
    if (!cfg.emitViewItem || templateName() !== "product" || !cfg.product) return;
    var p = cfg.product;

    var list = null;
    var saved = readViewLists()[p.handle];
    if (saved && Date.now() - saved.t <= VIEW_LIST_TTL) {
      list = { name: saved.name, id: saved.id };
    }

    var item = specItem(
      {
        productId: p.id,
        variantId: p.variantId,
        name: p.title,
        brand: p.vendor,
        category: p.type,
        price: p.price,
        variant: p.variantTitle,
        quantity: 1,
        sku: p.sku
      },
      list
    );
    pushSpecEvent("view_item", "product_viewed", "product", item.price, [item]);
  }

  // --- fetch interception (covers 8 of 9 paths, incl. the grid stepper) ---
  if (window.fetch && !window.fetch.__ga4AtcWrapped) {
    var originalFetch = window.fetch;
    var ga4Fetch = function (input) {
      var init = arguments[1];
      var url = typeof input === "string" ? input : input && input.url ? input.url : "";
      var track = isCartAddUrl(url) && !isFreeGiftRequest(input, init);

      var promise = originalFetch.apply(this, arguments);
      if (!track) return promise;

      promise
        .then(function (response) {
          if (!response || !response.ok) return; // failed add => no event
          // clone(): never consume the body the caller is waiting on.
          return response
            .clone()
            .json()
            .then(pushAddToCart)
            .catch(function () {});
        })
        .catch(function () {});

      return promise;
    };
    ga4Fetch.__ga4AtcWrapped = true;
    window.fetch = ga4Fetch;
  }

  // --- XHR interception (older jQuery-based add paths) ---
  if (window.XMLHttpRequest && !XMLHttpRequest.prototype.__ga4AtcWrapped) {
    var originalOpen = XMLHttpRequest.prototype.open;
    var originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      this.__ga4IsAdd = isCartAddUrl(url);
      return originalOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function () {
      if (this.__ga4IsAdd) {
        this.addEventListener("load", function () {
          if (this.status < 200 || this.status >= 300) return;
          try {
            pushAddToCart(JSON.parse(this.responseText));
          } catch (e) {
            /* non-JSON response (native form post) — ignore */
          }
        });
      }
      return originalSend.apply(this, arguments);
    };

    XMLHttpRequest.prototype.__ga4AtcWrapped = true;
  }

  /* ---------------------------------------------------------------
   * view_cart
   * ---------------------------------------------------------------
   * Two surfaces: the slide-out drawer, and the standalone /cart page.
   */

  // (a) Drawer. custom-cart-drawer.js toggles the "active" class on the
  // <cart-drawer> element in open()/close(). Observing the attribute covers
  // every entry point (header icon, post-add auto-open, programmatic
  // openCartDrawer()) without patching open()'s early-return branches.
  function watchDrawer(drawer) {
    if (!drawer || drawer.__ga4CartWatched) return;
    drawer.__ga4CartWatched = true;

    var wasActive = drawer.classList.contains("active");

    var observer = new MutationObserver(function () {
      var isActive = drawer.classList.contains("active");
      if (isActive === wasActive) return;
      wasActive = isActive;
      if (isActive) pushFromCart("view_cart");
    });

    observer.observe(drawer, { attributes: true, attributeFilter: ["class"] });

    if (wasActive) pushFromCart("view_cart");
  }

  function initDrawer() {
    var drawer = document.querySelector("cart-drawer");
    if (drawer) {
      watchDrawer(drawer);
      return;
    }

    // The drawer element may be injected late (section re-render). Watch for it
    // once, then disconnect.
    var bodyObserver = new MutationObserver(function () {
      var found = document.querySelector("cart-drawer");
      if (found) {
        bodyObserver.disconnect();
        watchDrawer(found);
      }
    });
    bodyObserver.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }

  // (b) Standalone /cart page load.
  function initCartPage() {
    var template = (window.ga4Config && window.ga4Config.template) || "";
    if (template !== "cart") return;
    pushFromCart("view_cart");
  }

  function init() {
    initDrawer();
    initCartPage();
    initViewItem();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
